import { apiClient } from './client';
import type {
  VenueRoomWithConfigs,
  CreateVenueRoomDto,
  UpdateVenueRoomDto,
  RoomSeatingConfig,
  CreateSeatingConfigDto,
  ExtractedRoom
} from '@/types/venue-room.types';

class VenueRoomService {
  /**
   * Get all rooms for a venue
   */
  async getRooms(venueId: number): Promise<VenueRoomWithConfigs[]> {
    const response = await apiClient.get(`/venues/${venueId}/rooms`);
    return response.data.data;
  }

  /**
   * Get a specific room with seating configs
   */
  async getRoom(venueId: number, roomId: number): Promise<VenueRoomWithConfigs> {
    const response = await apiClient.get(`/venues/${venueId}/rooms/${roomId}`);
    return response.data.data;
  }

  /**
   * Create a new room in a venue
   */
  async createRoom(venueId: number, data: CreateVenueRoomDto): Promise<VenueRoomWithConfigs> {
    const response = await apiClient.post(`/venues/${venueId}/rooms`, data);
    return response.data.data;
  }

  /**
   * Update a room
   */
  async updateRoom(venueId: number, roomId: number, data: UpdateVenueRoomDto): Promise<VenueRoomWithConfigs> {
    const response = await apiClient.put(`/venues/${venueId}/rooms/${roomId}`, data);
    return response.data.data;
  }

  /**
   * Soft-delete a room
   */
  async deleteRoom(venueId: number, roomId: number): Promise<void> {
    await apiClient.delete(`/venues/${venueId}/rooms/${roomId}`);
  }

  /**
   * Add a seating configuration to a room
   */
  async addSeatingConfig(venueId: number, roomId: number, data: CreateSeatingConfigDto): Promise<RoomSeatingConfig> {
    const response = await apiClient.post(`/venues/${venueId}/rooms/${roomId}/seating-configs`, data);
    return response.data.data;
  }

  /**
   * Remove a seating configuration from a room
   */
  async removeSeatingConfig(venueId: number, roomId: number, configType: string): Promise<void> {
    await apiClient.delete(`/venues/${venueId}/rooms/${roomId}/seating-configs/${configType}`);
  }

  /**
   * Auto-detect rooms/spaces from a venue's website using AI.
   * Returns suggestions — not yet saved.
   */
  async autoDetectRooms(
    venueId: number,
    url?: string,
    options?: {
      deepScan?: boolean;
    }
  ): Promise<{ rooms: ExtractedRoom[]; message: string }> {
    const response = await apiClient.post(`/venues/${venueId}/rooms/auto-detect`, {
      url,
      deepScan: options?.deepScan === true,
    });
    return { rooms: response.data.data || [], message: response.data.message };
  }

  /**
   * Create multiple rooms at once (used after auto-detect review).
   */
  async bulkCreateRooms(venueId: number, rooms: CreateVenueRoomDto[]): Promise<VenueRoomWithConfigs[]> {
    const response = await apiClient.post(`/venues/${venueId}/rooms/bulk`, { rooms });
    return response.data.data;
  }
}

export const venueRoomService = new VenueRoomService();
